import React, { useState, useEffect, useCallback } from 'react';
import { BellRing, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getPermissionState, isPushSupported, subscribeToPush } from '../utils/pushNotifications';

const dismissKey = (userId) => `csm_push_prompt_dismissed_${userId}`;

/**
 * Professional, low-pressure prompt to opt into browser push notifications.
 * Shown once per account (not the raw native permission dialog on load) —
 * appears a few seconds after login so it doesn't feel like a popup ambush.
 */
const PushPermissionPrompt = () => {
  const { user, token } = useAuth();
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (!user || !isPushSupported()) return;
    const alreadyDecided = getPermissionState() !== 'default';
    const dismissed = (() => {
      try {
        return localStorage.getItem(dismissKey(user.id)) === 'true';
      } catch {
        return false;
      }
    })();
    if (alreadyDecided || dismissed) return;

    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [user]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      if (user) localStorage.setItem(dismissKey(user.id), 'true');
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }, [user]);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      await subscribeToPush(token);
      toast.success('Push notifications enabled on this browser');
    } catch (err) {
      toast.error(err.message || 'Could not enable push notifications');
    } finally {
      setEnabling(false);
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-permission-prompt"
      className="fixed bottom-6 right-6 z-50 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 p-5 animate-in slide-in-from-bottom-4 duration-300"
    >
      <button
        onClick={dismiss}
        data-testid="push-prompt-close"
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-navy/10 flex items-center justify-center">
          <BellRing size={20} className="text-brand-navy" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-slate-900">Stay on top of your tasks</h3>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            Get notified the moment a task is assigned, approved, or commented on — even when this tab isn&apos;t open.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={handleEnable}
          disabled={enabling}
          data-testid="push-prompt-enable"
          className="flex-1 px-3 py-2 rounded-lg btn-cta text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {enabling ? 'Enabling…' : 'Enable Notifications'}
        </button>
        <button
          onClick={dismiss}
          data-testid="push-prompt-dismiss"
          className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default PushPermissionPrompt;
